import React, { useEffect, useState } from "react";
import api from "../api/Api.js";
import "bootstrap/dist/css/bootstrap.min.css";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import {
  MapPin,
  Phone,
  Clock,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  User,
  CarFront,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  Activity,
} from "lucide-react";

const SOSAdminPanel = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Driver");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // --- API LOGIC ---
  const fetchAlerts = async () => {
    try {
      const response = await api.get("/sosadmin/all");
      if (response.data.success) {
        setAlerts(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching alerts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 10000);
    return () => clearInterval(interval);
  }, []);

  const filteredAlerts = alerts.filter((a) => a.type === activeTab);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAlerts = filteredAlerts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAlerts.length / itemsPerPage);

  // --- TOGGLE STATUS WITH SWEETALERT ---
  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Pending" ? "Resolved" : "Pending";
    const isResolving = newStatus === "Resolved";

    const result = await Swal.fire({
      title: isResolving ? "Resolve Alert?" : "Re-activate Alert?",
      text: isResolving
        ? "Are you sure this emergency has been handled?"
        : "This will move the alert back to the pending queue.",
      icon: isResolving ? "question" : "warning",
      showCancelButton: true,
      confirmButtonColor: isResolving ? "#10b981" : "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: isResolving ? "Yes, Resolve it" : "Yes, Re-activate",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "rounded-4 shadow-lg fs-6",
      },
    });

    if (!result.isConfirmed) return;

    try {
      await api.put(`/sosadmin/update-status/${id}`, { status: newStatus });

      setAlerts((prev) =>
        prev.map((a) => (a.sos_id === id ? { ...a, status: newStatus } : a)),
      );

      Swal.fire({
        title: isResolving ? "Resolved!" : "Activated!",
        text: `Status updated successfully.`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to update status");
    }
  };

  // --- MAP FUNCTION ---
  const openMap = (lat, lng) => {
    if (!lat || !lng) {
      toast.error("Location coordinates missing");
      return;
    }
    const url = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const formatDate = (date) =>
    new Date(date).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className="min-vh-100 bg-white py-1 font-sans">
      <div className="container-fluid" style={{ maxWidth: "1500px" }}>
        {/* --- COMPACT HEADER SECTION --- */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3 bg-white p-3 rounded-3 shadow-sm border border-light">
          {/* Title & Stats */}
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded bg-danger bg-opacity-10 text-danger"
              style={{ width: "42px", height: "42px" }}
            >
              <ShieldAlert size={22} />
            </div>
            <div>
              <h5 className="fw-bold text-dark mb-0 tracking-tight">
                SOS Emergency Center
              </h5>
              <div
                className="d-flex gap-2 mt-1 text-muted fw-medium"
                style={{ fontSize: "0.75rem" }}
              >
                <span className="d-flex align-items-center gap-1">
                  <span
                    className="badge bg-danger rounded-pill px-2"
                    style={{ fontSize: "0.65rem" }}
                  >
                    {
                      filteredAlerts.filter((a) => a.status === "Pending")
                        .length
                    }
                  </span>{" "}
                  Active Alerts
                </span>
                <span className="d-flex align-items-center gap-1">
                  <span
                    className="badge bg-success rounded-pill px-2"
                    style={{ fontSize: "0.65rem" }}
                  >
                    {
                      filteredAlerts.filter((a) => a.status === "Resolved")
                        .length
                    }
                  </span>{" "}
                  Resolved
                </span>
              </div>
            </div>
          </div>

          {/* Controls (Tabs & Refresh) */}
          <div className="d-flex align-items-center gap-2">
            {/* Segmented Tab Control */}
            <div className="bg-light p-1 rounded-pill d-flex border">
              {["Driver", "Customer"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setCurrentPage(1);
                  }}
                  className={`btn btn-sm rounded-pill px-3 py-1 fw-semibold d-flex align-items-center gap-1 border-0 ${
                    activeTab === tab
                      ? "bg-dark text-white shadow-sm"
                      : "bg-transparent text-muted"
                  }`}
                  style={{ fontSize: "0.8rem", transition: "all 0.2s" }}
                >
                  {tab === "Driver" ? (
                    <CarFront size={14} />
                  ) : (
                    <User size={14} />
                  )}
                  {tab}
                </button>
              ))}
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchAlerts}
              className="btn btn-sm btn-white bg-white rounded-circle shadow-sm border d-flex align-items-center justify-content-center hover-scale ms-1"
              style={{ width: "34px", height: "34px" }}
              title="Refresh Data"
            >
              <RefreshCw
                size={14}
                className={`text-dark ${loading ? "spinner-border spinner-border-sm border-0" : ""}`}
                style={loading ? { animation: "spin 1s linear infinite" } : {}}
              />
            </button>
          </div>
        </div>

        {/* --- COMPACT TABLE CARD --- */}
        <div className="card shadow-sm border-0 rounded-3 overflow-hidden bg-white">
          <div className="table-responsive">
            <table
              className="table table-sm table-hover align-middle mb-0"
              style={{ minWidth: "900px" }}
            >
              <thead className="bg-light border-bottom">
                <tr>
                  <th
                    className="py-2 px-3 text-primary text-uppercase fw-bold border-0"
                    style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
                  >
                    Identity
                  </th>
                  <th
                    className="py-2 px-3 text-primary text-uppercase fw-bold border-0"
                    style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
                  >
                    Contact
                  </th>
                  <th
                    className="py-2 px-3 text-primary text-uppercase fw-bold border-0"
                    style={{
                      fontSize: "0.7rem",
                      letterSpacing: "0.5px",
                      minWidth: "220px",
                    }}
                  >
                    Location & Time
                  </th>
                  <th
                    className="py-2 px-3 text-primary text-uppercase fw-bold border-0 text-center"
                    style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
                  >
                    Map
                  </th>
                  <th
                    className="py-2 px-3 text-primary text-uppercase fw-bold border-0 text-end"
                    style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      <div
                        className="spinner-border spinner-border-sm text-primary mb-1"
                        role="status"
                      ></div>
                      <div className="text-muted small">Loading...</div>
                    </td>
                  </tr>
                ) : currentAlerts.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-5">
                      <CheckCircle
                        size={28}
                        className="text-success mb-2 opacity-75"
                      />
                      <h6 className="fw-bold text-dark mb-1">All Clear!</h6>
                      <p className="text-muted small mb-0">
                        No active {activeTab.toLowerCase()} alerts.
                      </p>
                    </td>
                  </tr>
                ) : (
                  currentAlerts.map((alert) => (
                    <tr
                      key={alert.sos_id}
                      className="transition-hover border-bottom"
                      style={{
                        backgroundColor:
                          alert.status === "Pending"
                            ? "rgba(254, 242, 242, 0.6)"
                            : "white",
                        borderLeft:
                          alert.status === "Pending"
                            ? "3px solid #ef4444"
                            : "3px solid transparent",
                      }}
                    >
                      {/* Identity (Compact) */}
                      <td className="px-3 py-2">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm flex-shrink-0"
                            style={{
                              width: "32px",
                              height: "32px",
                              fontSize: "13px",
                              background:
                                activeTab === "Driver"
                                  ? "linear-gradient(135deg, #3b82f6, #2563eb)"
                                  : "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                            }}
                          >
                            {alert.name?.charAt(0).toUpperCase()}
                          </div>
                          <div className="lh-sm">
                            <div
                              className="fw-bold text-dark"
                              style={{ fontSize: "0.85rem" }}
                            >
                              {alert.name}
                            </div>
                            <span
                              className="text-muted"
                              style={{ fontSize: "0.7rem", fontWeight: "500" }}
                            >
                              REGD: {alert.user_id}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Contact (Sleek Tag) */}
                      <td className="px-3 py-2">
                        <a
                          href={`tel:${alert.phone}`}
                          className="text-decoration-none d-inline-flex align-items-center gap-1 bg-light border px-2 py-1 rounded"
                          style={{
                            fontSize: "0.8rem",
                            color: "#1e293b",
                            fontWeight: "600",
                          }}
                        >
                          <Phone size={12} className="text-primary" />
                          {alert.phone}
                        </a>
                      </td>

                      {/* Location & Time (Tight spacing) */}
                      <td className="px-3 py-2">
                        <div className="d-flex align-items-start gap-1 lh-sm">
                          <MapPin
                            size={14}
                            className="text-danger mt-1 flex-shrink-0"
                          />
                          <div>
                            <span
                              className="d-block text-dark fw-medium mb-1"
                              style={{ fontSize: "0.8rem" }}
                            >
                              {alert.location || "Coordinates Only"}
                            </span>
                            <span
                              className="text-muted d-flex align-items-center gap-1"
                              style={{ fontSize: "0.7rem" }}
                            >
                              <Clock size={10} className="text-secondary" />{" "}
                              {formatDate(alert.time)}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Map Action (Small Icon Button) */}
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => openMap(alert.lat, alert.lng)}
                          className="btn p-0 rounded-circle d-inline-flex align-items-center justify-content-center shadow-sm hover-scale"
                          style={{
                            width: "36px",
                            height: "36px",
                            backgroundColor: "#eff6ff", // Soft light-blue background
                            border: "1px solid #bfdbfe", // Subtle blue border
                            color: "#2563eb", // Primary blue icon
                            transition: "all 0.2s ease-in-out",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#3b82f6"; // Solid blue on hover
                            e.currentTarget.style.color = "white"; // Icon turns white
                            e.currentTarget.style.borderColor = "#3b82f6";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#eff6ff";
                            e.currentTarget.style.color = "#2563eb";
                            e.currentTarget.style.borderColor = "#bfdbfe";
                          }}
                          title="View on Google Maps"
                        >
                          <ExternalLink size={16} strokeWidth={2.5} />
                        </button>
                      </td>

                      {/* Status Toggle (Compact Badge Button) */}
                      <td className="px-3 py-2 text-end">
                        <button
                          onClick={() =>
                            toggleStatus(alert.sos_id, alert.status)
                          }
                          className={`btn btn-sm fw-bold d-inline-flex align-items-center gap-1 px-2 py-1 shadow-sm border hover-scale ${
                            alert.status === "Pending"
                              ? "btn-danger"
                              : "bg-white text-success border-success"
                          }`}
                          style={{ fontSize: "0.75rem", borderRadius: "6px" }}
                        >
                          {alert.status === "Pending" ? (
                            <>
                              <Activity size={12} className="animate-pulse" />
                              <span>Resolve</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle size={12} />
                              <span>Resolved</span>
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* --- COMPACT PAGINATION FOOTER --- */}
          {totalPages > 1 && (
            <div className="card-footer bg-white py-2 px-3 border-top d-flex justify-content-between align-items-center">
              <span
                className="text-muted"
                style={{ fontSize: "0.75rem", fontWeight: "500" }}
              >
                Showing{" "}
                <strong className="text-dark">{indexOfFirstItem + 1}</strong> to{" "}
                <strong className="text-dark">
                  {Math.min(indexOfLastItem, filteredAlerts.length)}
                </strong>{" "}
                of{" "}
                <strong className="text-dark">{filteredAlerts.length}</strong>
              </span>

              <div className="d-flex align-items-center gap-1">
                <button
                  className="btn btn-sm btn-light border p-0 d-flex align-items-center justify-content-center hover-scale"
                  style={{ width: "26px", height: "26px", borderRadius: "4px" }}
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft
                    size={14}
                    className={currentPage === 1 ? "text-muted" : "text-dark"}
                  />
                </button>

                <span
                  className="d-flex align-items-center justify-content-center px-2 fw-bold text-dark"
                  style={{ fontSize: "0.8rem", minWidth: "32px" }}
                >
                  {currentPage}
                </span>

                <button
                  className="btn btn-sm btn-light border p-0 d-flex align-items-center justify-content-center hover-scale"
                  style={{ width: "26px", height: "26px", borderRadius: "4px" }}
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight
                    size={14}
                    className={
                      currentPage === totalPages ? "text-muted" : "text-dark"
                    }
                  />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* --- CUSTOM STYLES --- */}
      <style>{`
        .tracking-tight { letter-spacing: -0.025em; }
        .transition-hover { transition: background-color 0.15s ease; }
        .transition-hover:hover { background-color: #f8fafc !important; }
        .hover-scale:hover:not(:disabled) { transform: translateY(-1px); }
        .hover-scale:active:not(:disabled) { transform: translateY(0); }
        
        .animate-pulse { animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
        @keyframes pulse { 
          0%, 100% { opacity: 1; transform: scale(1); } 
          50% { opacity: 0.6; transform: scale(1.1); } 
        }
        
        @keyframes spin { 
          from { transform: rotate(0deg); } 
          to { transform: rotate(360deg); } 
        }
      `}</style>
    </div>
  );
};

export default SOSAdminPanel;